import { asArray, asInt, useQuery } from "../../utils/hooks/useQuery";
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
  ListRowProps,
  ListRowRenderer,
} from "react-virtualized";
import { buildJobInsightsGeoJson } from "portal/utils/exportJobInsightsGeoJson";
import {
  Button,
  Card,
  CardActionArea,
  Checkbox,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import { CarbonDateRangePicker } from "portal/components/CarbonDateRangePicker";
import { classes } from "portal/theme/theme";
import { DATE_PATH_FORMAT } from "portal/utils/dates";
import { DateRange } from "@mui/x-date-pickers-pro";
import { DateTime } from "luxon";
import { downloadJson } from "portal/utils/download";
import { Map as HeatmapMap } from "portal/components/map/Map";
import { MetricDataSource } from "../../../../../protos/golang/generated/ts2/portal/metrics";
import { NoScroll } from "portal/components/Page";
import { portalApi } from "portal/state/portalApi";
import { PortalJob } from "protos/portal/jobs";
import { portalLanguageOrDefault } from "portal/i18nConstants";
import { RobotMultiSelector } from "portal/components/robots/RobotMultiSelector";
import { SearchField } from "portal/components/header/SearchField";
import { skipToken } from "@reduxjs/toolkit/query";
import { titleCase } from "portal/utils/strings";
import { useHeatmapBlocks } from "./useHeatmapBlocks";
import { useJigsawMetrics } from "portal/utils/hooks/useJigsawMetrics";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { ViewPlaceholder } from "portal/components/ViewPlaceholder";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import ExportIcon from "@mui/icons-material/FileDownload";
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

interface RobotJobs {
  serial: string;
  customerSerial: string;
  jobs: PortalJob[];
  isLoading: boolean;
}

// Above this many selected jobs, we skip the heatmap fetch and show a
// "too many to render" message. The block overlay gets O(N^2)-ish in tile
// count and browsers start to choke around this threshold.
const MAX_JOBS_TO_RENDER = 100;

// One item in the flat virtualized sidebar list.
type SidebarItem =
  | { kind: "header"; robotId: number; customerSerial: string }
  | { kind: "loading"; robotId: number }
  | { kind: "empty"; robotId: number }
  | { kind: "job"; robotId: number; job: PortalJob };

const filterJobs = (jobs: PortalJob[], filter: string): PortalJob[] => {
  if (!filter) {
    return jobs;
  }
  const lower = filter.toLowerCase();
  return jobs.filter(
    (job) =>
      (job.name && job.name.toLowerCase().includes(lower)) ||
      job.jobId.toLowerCase().includes(lower)
  );
};

const JobCard: FunctionComponent<{
  job: PortalJob;
  isSelected: boolean;
  onToggle: (jobId: string) => void;
}> = ({ job, isSelected, onToggle }) => {
  const { i18n } = useTranslation();
  const jobId = job.jobId;
  return (
    <Card
      variant="outlined"
      className={classes("transition-opacity", {
        "opacity-50": !isSelected,
      })}
    >
      <CardActionArea
        className="flex items-center justify-start px-2 py-1 gap-1"
        onClick={() => onToggle(jobId)}
      >
        <Checkbox
          checked={isSelected}
          size="small"
          disableRipple
          tabIndex={-1}
        />
        <div className="flex flex-col min-w-0">
          <Typography variant="body2" noWrap>
            {job.name || jobId}
          </Typography>
          {job.timestampMs ? (
            <Typography variant="caption" color="textSecondary">
              {DateTime.fromMillis(job.timestampMs).toLocaleString(
                DateTime.DATETIME_MED,
                { locale: portalLanguageOrDefault(i18n.language) }
              )}
            </Typography>
          ) : undefined}
        </div>
      </CardActionArea>
    </Card>
  );
};

const _HeatmapReports: FunctionComponent = () => {
  const today = DateTime.local();

  // State
  const [selectedRobotIds = [], setSelectedRobotIds] = useQuery(
    "robots",
    asArray(asInt)
  );
  const [dateRange, setDateRange] = useState<DateRange<DateTime>>([
    today.minus({ days: 7 }),
    today,
  ]);
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set());

  // Resolve robot IDs to serials
  const { data: robots } = useQueryPopups(
    portalApi.endpoints.listRobots.useQuery({})
  );

  const selectedRobots = useMemo(() => {
    if (!robots) {
      return [];
    }
    return selectedRobotIds
      .map((id) => {
        const summary = robots.find((r) => r.robot?.db?.id === id);
        return summary
          ? {
              id,
              serial: summary.robot?.serial ?? "",
              customerSerial: summary.robot?.customerSerial ?? "",
            }
          : undefined;
      })
      .filter(
        (r): r is { id: number; serial: string; customerSerial: string } =>
          r !== undefined && r.serial !== ""
      );
  }, [robots, selectedRobotIds]);

  const startDate = dateRange[0]?.toFormat(DATE_PATH_FORMAT);
  const endDate = dateRange[1]?.toFormat(DATE_PATH_FORMAT);

  return (
    <HeatmapReportsInner
      selectedRobots={selectedRobots}
      startDate={startDate}
      endDate={endDate}
      selectedRobotIds={selectedRobotIds}
      setSelectedRobotIds={setSelectedRobotIds}
      dateRange={dateRange}
      setDateRange={setDateRange}
      selectedJobIds={selectedJobIds}
      setSelectedJobIds={setSelectedJobIds}
    />
  );
};

// Inner component that issues a single batched jobs query for all
// selected robots and renders a virtualized sidebar over the result.
const HeatmapReportsInner: FunctionComponent<{
  selectedRobots: { id: number; serial: string; customerSerial: string }[];
  startDate: string | undefined;
  endDate: string | undefined;
  selectedRobotIds: number[];
  setSelectedRobotIds: (ids: number[]) => void;
  dateRange: DateRange<DateTime>;
  setDateRange: (range: DateRange<DateTime>) => void;
  selectedJobIds: Set<string>;
  setSelectedJobIds: React.Dispatch<React.SetStateAction<Set<string>>>;
}> = ({
  selectedRobots,
  startDate,
  endDate,
  selectedRobotIds,
  setSelectedRobotIds,
  dateRange,
  setDateRange,
  selectedJobIds,
  setSelectedJobIds,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const onToggleJob = useCallback(
    (jobId: string) => {
      setSelectedJobIds((previous) => {
        const next = new Set(previous);
        if (next.has(jobId)) {
          next.delete(jobId);
        } else {
          next.add(jobId);
        }
        return next;
      });
    },
    [setSelectedJobIds]
  );

  // In-progress robot selection while the multi-select is open; committed on close
  const [robotPickerInput, setRobotPickerInput] =
    useState<number[]>(selectedRobotIds);
  useEffect(() => {
    setRobotPickerInput(selectedRobotIds);
  }, [selectedRobotIds]);
  const onRobotSelectorOpen = useCallback(() => {
    setRobotPickerInput(selectedRobotIds);
  }, [selectedRobotIds]);
  const onRobotSelectorClose = useCallback(() => {
    setSelectedRobotIds(robotPickerInput);
  }, [robotPickerInput, setSelectedRobotIds]);

  // One batched fetch for all selected robots.
  const serials = useMemo(
    () => selectedRobots.map((r) => r.serial),
    [selectedRobots]
  );
  const useJigsaw = useJigsawMetrics();
  const queryArg =
    serials.length > 0 && startDate && endDate
      ? { serials, startDate, endDate, useJigsaw }
      : skipToken;
  const { data: jobsBySerial, isLoading: isJobsLoading } = useQueryPopups(
    portalApi.endpoints.batchListRobotJobs.useQuery(queryArg)
  );

  // Reshape the batch response into a by-robot-id map so the sidebar
  // can still key sections/items by robot.id.
  const jobsByRobot = useMemo<Map<number, RobotJobs>>(() => {
    const map = new Map<number, RobotJobs>();
    for (const robot of selectedRobots) {
      const jobs = jobsBySerial?.[robot.serial];
      map.set(robot.id, {
        serial: robot.serial,
        customerSerial: robot.customerSerial,
        jobs: jobs ?? [],
        isLoading: !jobsBySerial && isJobsLoading,
      });
    }
    return map;
  }, [selectedRobots, jobsBySerial, isJobsLoading]);

  const [jobFilter, setJobFilter] = useState("");

  // Flat list of items for the virtualized sidebar.
  const sidebarItems = useMemo<SidebarItem[]>(() => {
    const items: SidebarItem[] = [];
    for (const robot of selectedRobots) {
      const data = jobsByRobot.get(robot.id);
      const customerSerial = data?.customerSerial ?? robot.customerSerial;
      items.push({ kind: "header", robotId: robot.id, customerSerial });
      if (!data || data.isLoading) {
        items.push({ kind: "loading", robotId: robot.id });
        continue;
      }
      const filtered = filterJobs(data.jobs, jobFilter);
      if (filtered.length === 0) {
        items.push({ kind: "empty", robotId: robot.id });
      } else {
        for (const job of filtered) {
          items.push({ kind: "job", robotId: robot.id, job });
        }
      }
    }
    return items;
  }, [selectedRobots, jobsByRobot, jobFilter]);

  // Label map for the map overlay, derived from loaded jobs.
  const jobNames = useMemo<Map<string, string>>(() => {
    const locale = portalLanguageOrDefault(i18n.language);
    const map = new Map<string, string>();
    for (const data of jobsByRobot.values()) {
      for (const job of data.jobs) {
        if (!job.jobId) {
          continue;
        }
        const prefix = data.customerSerial;
        const isUuid = !job.name || /^[\da-f]{8}-[\da-f]{4}-/i.test(job.name);
        if (isUuid) {
          const parts: string[] = [];
          if (prefix) {
            parts.push(prefix);
          }
          if (job.timestampMs) {
            parts.push(
              DateTime.fromMillis(job.timestampMs).toLocaleString(
                DateTime.DATETIME_MED,
                { locale }
              )
            );
          }
          const label =
            parts.length > 0 ? parts.join(" — ") : job.name || job.jobId;
          map.set(job.jobId, label);
        } else {
          map.set(job.jobId, prefix ? `${prefix} — ${job.name}` : job.name);
        }
      }
    }
    return map;
  }, [jobsByRobot, i18n.language]);

  // jobId -> robot serial, for the GeoJSON export (jigsaw blocks carry no serial).
  const jobSerials = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    for (const data of jobsByRobot.values()) {
      for (const job of data.jobs) {
        if (job.jobId) {
          map.set(job.jobId, data.serial);
        }
      }
    }
    return map;
  }, [jobsByRobot]);

  // All job IDs visible under the current filter, across all robots.
  const allVisibleJobIds = useMemo(() => {
    const ids: string[] = [];
    for (const data of jobsByRobot.values()) {
      for (const job of filterJobs(data.jobs, jobFilter)) {
        if (job.jobId) {
          ids.push(job.jobId);
        }
      }
    }
    return ids;
  }, [jobsByRobot, jobFilter]);

  // Lifted so we can re-enable auto-fit when the selection changes via Select All —
  // the Map turns these off on user pan/zoom and would otherwise stay frozen.
  const [isAutoPanning, setIsAutoPanning] = useState(true);
  const [isAutoZooming, setIsAutoZooming] = useState(true);

  const onSelectAll = useCallback(() => {
    // Replace (don't union) so re-filtering + Select All discards stale selections
    // from previous filters. Individual checkbox clicks still accumulate.
    setSelectedJobIds(new Set(allVisibleJobIds));
    setIsAutoPanning(true);
    setIsAutoZooming(true);
  }, [allVisibleJobIds, setSelectedJobIds]);

  const onUnselectAll = useCallback(() => {
    setSelectedJobIds(new Set());
  }, [setSelectedJobIds]);

  // Clear selected jobs when robots or date range changes.
  useEffect(() => {
    setSelectedJobIds(new Set());
    setJobFilter("");
  }, [selectedRobots.length, dateRange, setSelectedJobIds]);

  const selectedJobIdsArray = useMemo(
    () => [...selectedJobIds],
    [selectedJobIds]
  );
  const tooManyJobsToRender = selectedJobIds.size > MAX_JOBS_TO_RENDER;
  // Skip the fetch entirely when over the render limit — sending 200+ job
  // UUIDs just to discard the result wastes the request and the map would
  // choke anyway.
  const jobIdsForBlocks = useMemo(
    () => (tooManyJobsToRender ? [] : selectedJobIdsArray),
    [tooManyJobsToRender, selectedJobIdsArray]
  );
  const {
    blocks,
    blockLabels,
    blocksByJobId,
    isLoading: isBlocksLoading,
  } = useHeatmapBlocks(jobIdsForBlocks, jobNames);

  const onExportGeoJson = useCallback(() => {
    if (!blocksByJobId) {
      return;
    }
    const geojson = buildJobInsightsGeoJson({
      blocksByJobId,
      jobNames,
      jobSerials,
      // Match the units the map uses so exported swaths are geometrically identical.
      units: "millimeters",
    });
    const firstRobot = selectedRobots[0];
    const robotPart =
      selectedRobots.length === 1 && firstRobot
        ? firstRobot.serial
        : `${selectedRobots.length}-robots`;
    const fmt = (d: DateTime | null): string =>
      d ? d.toFormat(DATE_PATH_FORMAT) : "";
    const [from, to] = dateRange;
    const filename = `job-insights-${robotPart}-${fmt(from)}-${fmt(
      to
    )}.geojson`.replaceAll(/[^\w.-]+/g, "_");
    downloadJson(filename, geojson, "application/geo+json");
  }, [blocksByJobId, jobNames, jobSerials, selectedRobots, dateRange]);

  const listRef = useRef<List | null>(null);
  // for virtualized row height calculations
  const cellCacheRef = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 56,
    })
  );
  const cellCache = cellCacheRef.current;
  const prevListWidthRef = useRef(0);

  useEffect(() => {
    cellCache.clearAll();
    listRef.current?.recomputeRowHeights();
  }, [sidebarItems, cellCache]);

  const renderRow: ListRowRenderer = useCallback(
    ({ index, key, parent, style }: ListRowProps) => {
      const item = sidebarItems[index];
      if (!item) {
        return <div key={key} style={style} />;
      }
      return (
        <CellMeasurer
          cache={cellCache}
          columnIndex={0}
          key={key}
          parent={parent}
          rowIndex={index}
        >
          {({ registerChild }) => (
            <div
              ref={registerChild as React.Ref<HTMLDivElement>}
              style={style}
              className={item.kind === "empty" ? "pb-2" : undefined}
            >
              {renderSidebarItem(item, selectedJobIds, onToggleJob, t)}
            </div>
          )}
        </CellMeasurer>
      );
    },
    [cellCache, sidebarItems, selectedJobIds, onToggleJob, t]
  );

  return (
    <NoScroll className="gap-4 pt-2">
      {/* Top controls row */}
      <div className="flex gap-4 items-center flex-wrap flex-shrink-0">
        <RobotMultiSelector
          className="min-w-64"
          selectedRobots={robotPickerInput}
          onChange={setRobotPickerInput}
          onOpen={onRobotSelectorOpen}
          onClose={onRobotSelectorClose}
        />
        <CarbonDateRangePicker
          className="w-80"
          calendars={1}
          disableFuture
          value={dateRange}
          onChange={setDateRange}
        />
      </div>
      {/* Main content: sidebar + map */}
      <div className="flex flex-col md:flex-row gap-4 flex-grow min-h-0">
        {/* Left sidebar: job list */}
        <div className="w-full md:w-80 flex flex-col gap-3 flex-shrink-0">
          {selectedRobots.length === 0 ? (
            <ViewPlaceholder
              text={titleCase(t("views.reports.tools.robotsLabel.select"))}
            />
          ) : (
            <>
              <SearchField
                value={jobFilter}
                onChange={setJobFilter}
                className="flex-shrink-0"
              />
              <div className="h-96 md:h-auto md:flex-1 md:min-h-0">
                <AutoSizer>
                  {({ height, width }) => {
                    // invalidate the height cache when the list's width changes
                    if (prevListWidthRef.current !== width) {
                      if (prevListWidthRef.current !== 0) {
                        cellCache.clearAll();
                        listRef.current?.recomputeRowHeights();
                      }
                      prevListWidthRef.current = width;
                    }
                    return (
                      <List
                        ref={(r) => {
                          listRef.current = r;
                        }}
                        deferredMeasurementCache={cellCache}
                        height={height}
                        width={width}
                        rowCount={sidebarItems.length}
                        rowHeight={cellCache.rowHeight}
                        rowRenderer={renderRow}
                        overscanRowCount={6}
                      />
                    );
                  }}
                </AutoSizer>
              </div>
              <div className="flex gap-2 flex-shrink-0 items-center">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onSelectAll}
                  disabled={allVisibleJobIds.length === 0}
                >
                  {t("utils.actions.selectAll")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onUnselectAll}
                  disabled={selectedJobIds.size === 0}
                >
                  {t("utils.actions.selectNone")}
                </Button>
                <Tooltip
                  title={
                    tooManyJobsToRender
                      ? t("views.reports.jobInsights.exportGeoJsonTooMany", {
                          max: MAX_JOBS_TO_RENDER,
                        })
                      : t("views.reports.jobInsights.exportGeoJsonTooltip")
                  }
                >
                  {/* span wrapper so the tooltip shows even when the button is disabled */}
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ExportIcon />}
                      onClick={onExportGeoJson}
                      disabled={
                        selectedJobIds.size === 0 ||
                        isBlocksLoading ||
                        tooManyJobsToRender ||
                        !blocksByJobId
                      }
                    >
                      {t("views.reports.jobInsights.exportGeoJson")}
                    </Button>
                  </span>
                </Tooltip>
                <div className="ml-auto flex flex-col items-end leading-tight">
                  <Typography variant="caption" color="textSecondary">
                    {t("views.reports.jobInsights.selectedCount", {
                      count: selectedJobIds.size,
                    })}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t("views.reports.jobInsights.availableCount", {
                      count: allVisibleJobIds.length,
                    })}
                  </Typography>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right: Map */}
        <div className="flex-grow min-h-[400px]">
          {tooManyJobsToRender ? (
            <div
              className={classes(
                "w-full h-[600px] max-h-[80vh] md:h-full md:max-h-full",
                "flex flex-col items-center justify-center rounded px-6"
              )}
              style={{
                backgroundColor: theme.palette.warning.light,
                color: theme.palette.warning.dark,
              }}
            >
              <Typography variant="h6" className="font-semibold text-center">
                {t("views.reports.jobInsights.tooManyToRender", {
                  max: MAX_JOBS_TO_RENDER,
                })}
              </Typography>
            </div>
          ) : (
            <HeatmapMap
              className="w-full flex-grow h-[600px] max-h-[80vh] md:h-full md:max-h-full"
              blockLabels={blockLabels}
              blocks={blocks}
              loading={isBlocksLoading}
              allowBorders
              hideRobots
              allowEmpty
              emptyErrorText={t("views.reports.jobInsights.selectJobs")}
              isAutoPanning={isAutoPanning}
              setIsAutoPanning={setIsAutoPanning}
              isAutoZooming={isAutoZooming}
              setIsAutoZooming={setIsAutoZooming}
              metricsDataSource={MetricDataSource.JIGSAW}
            />
          )}
        </div>
      </div>
    </NoScroll>
  );
};

const renderSidebarItem = (
  item: SidebarItem,
  selectedJobIds: Set<string>,
  onToggleJob: (jobId: string) => void,
  t: (key: string) => string
): React.ReactNode => {
  switch (item.kind) {
    case "header": {
      return (
        <Typography variant="subtitle2" className="px-1" color="textSecondary">
          {item.customerSerial}
        </Typography>
      );
    }
    case "loading": {
      return (
        <div className="flex justify-center py-4">
          <CircularProgress size={24} />
        </div>
      );
    }
    case "empty": {
      return (
        <Typography
          variant="body2"
          className="px-1"
          sx={{
            color: "text.disabled",
          }}
        >
          {t("views.reports.jobInsights.noJobs")}
        </Typography>
      );
    }
    case "job": {
      if (!item.job.jobId) {
        return;
      }
      return (
        <JobCard
          job={item.job}
          isSelected={selectedJobIds.has(item.job.jobId)}
          onToggle={onToggleJob}
        />
      );
    }
  }
};

export const HeatmapReports = withAuthenticationRequired(_HeatmapReports);
